import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react'
import { Dispatch, SetStateAction, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { CategoriesCheckboxTreeCategoryFragment } from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import { CheckboxGroup, CheckboxGroupItem } from '@/modules/shared/components/checkbox-group/CheckboxGroup'
import { Checkbox } from '@/modules/shared/components/checkbox/Checkbox'
import { ChevronDownIcon } from '@/modules/shared/icons/ChevronDownIcon'
import { classNames } from '@/modules/shared/utils/classNames'

interface CategoriesCheckboxTreeProps {
  category: CategoriesCheckboxTreeCategoryFragment
  selectedCategories: number[]
  setSelectedCategories: Dispatch<SetStateAction<number[]>>
  selectedParentCategories: number[]
  setSelectedParentCategories: Dispatch<SetStateAction<number[]>>
}

function CategoriesCheckboxTree({
  category,
  selectedCategories,
  setSelectedCategories,
  selectedParentCategories,
  setSelectedParentCategories,
}: CategoriesCheckboxTreeProps) {
  const { t } = useTranslation()
  const subCategories = category.categories?.unpaginated ?? []
  const result: number[] = subCategories
    .map((subCategory) => subCategory.id)
    .filter((id) => selectedCategories.includes(id))

  const [checkedList, setCheckedList] = useState<number[]>(result)
  const [indeterminate, setIndeterminate] = useState(
    checkedList.length > 0 && checkedList.length < subCategories.length
  )
  const [checkAll, setCheckAll] = useState(checkedList.length === subCategories.length)

  const removeDuplicates = (arr: number[]) => arr.filter((item, index) => arr.indexOf(item) === index)

  const onCheckOneChange = (list: number[], subCategoriesLength: number, categoryId: number) => {
    setCheckedList(list)
    setIndeterminate(Boolean(list.length) && list.length < subCategoriesLength)
    setCheckAll(list.length === subCategoriesLength)
    const subCategoryIds = subCategories.map((sub) => sub.id)
    const removedSameCategoryItems = selectedCategories.filter((item) => !subCategoryIds.includes(item))
    const concat = removedSameCategoryItems.concat(list)

    setSelectedCategories(removeDuplicates(concat))
    setSelectedParentCategories((prevParentCategories) => {
      if (Boolean(list.length) && list.length < subCategoriesLength)
        return removeDuplicates([...selectedParentCategories, categoryId])
      return prevParentCategories.filter((item) => item !== categoryId)
    })
  }

  const onCheckAllChange = (value: boolean, checkedList: number[], categoryId: number) => {
    setCheckedList(value ? checkedList : [])
    setIndeterminate(false)
    setCheckAll(value)

    setSelectedCategories((prevCategories) => {
      const concat = prevCategories.concat(checkedList)
      const findUnique = concat.filter((item, pos) => concat.indexOf(item) === pos)
      if (value) return findUnique
      return findUnique.filter((item) => !checkedList.includes(item))
    })
    setSelectedParentCategories((prevParentCategories) => {
      if (value) return removeDuplicates([...selectedParentCategories, categoryId])
      return prevParentCategories.filter((item) => item !== categoryId)
    })
  }

  return (
    <div className="flex flex-col gap-y-2">
      <Disclosure defaultOpen={false}>
        {({ open }) => (
          <>
            <div className="flex items-center justify-between rounded-md border bg-gray-100 p-2">
              <Checkbox
                aria-label={category.name?.toString()}
                isIndeterminate={indeterminate}
                onChange={(value) => {
                  const checkedList = subCategories.map((cat) => cat.id)
                  onCheckAllChange(value, checkedList, category.id)
                }}
                isSelected={checkAll}
              >
                {category.name}
              </Checkbox>
              <DisclosureButton className="flex grow justify-end gap-x-2 py-1">
                <div className="flex items-center gap-x-2">
                  {checkedList.length > 0 && (
                    <span className="text-sm text-gray-500">
                      {t('checkBoxTree.itemSelected', '{{ item }} Selected', { item: checkedList.length })}
                    </span>
                  )}
                  <ChevronDownIcon
                    className={classNames('size-5 transition-all duration-300', {
                      'rotate-180 transform': open,
                    })}
                  />
                </div>
              </DisclosureButton>
            </div>
            <DisclosurePanel>
              <div className="ml-8 mt-1 rounded-md border bg-gray-100">
                {/* CheckboxGroup and CheckGroupItem needs string, we have to cast original data to match */}
                <CheckboxGroup
                  aria-label={category.name}
                  value={checkedList.map((e) => e.toString())}
                  onChange={(list) => {
                    onCheckOneChange(
                      list.map((e) => Number(e)),
                      subCategories.length,
                      category.id
                    )
                  }}
                >
                  {subCategories.map((subCategory, index) => (
                    <div
                      key={subCategory.id}
                      className={classNames('p-2 first:rounded-t-md last:rounded-b-md', { 'border-t': index > 0 })}
                    >
                      <CheckboxGroupItem
                        key={subCategory.id}
                        aria-label={subCategory.name?.toString()}
                        value={subCategory.id.toString()}
                      >
                        {subCategory.name}
                      </CheckboxGroupItem>
                    </div>
                  ))}
                </CheckboxGroup>
              </div>
            </DisclosurePanel>
          </>
        )}
      </Disclosure>
    </div>
  )
}

export default CategoriesCheckboxTree
